// Market-label normalisation helpers for SportsEventClient. Wraps the
// rich Polymarket market metadata into the short labels the PM /event
// page renders ("Moneyline" not "Match Winner", "Map Handicap" not
// "Game Handicap" on CS2/Valorant, "Total Maps 2.5" on neg-risk CS2,
// "Set 1 Winner" instead of "Tournament: Player A vs Player B Set 1
// Winner"). All translation goes through a `tr` callback so callers
// pass `useT()` and get locale-correct copy.

/**
 * Rewrites a market title swapping each team's full legal name for its short
 * `alias`, so the sidebar header reads "Draw (Rayo Vallecano vs Espanyol)"
 * instead of the PM-provided "Draw (Rayo Vallecano de Madrid vs. RCD Espanyol
 * de Barcelona)". Falls through to the original title when no alias exists.
 */
export function shortenTitleWithAliases(
  title: string,
  home?: { name?: string; alias?: string | null },
  away?: { name?: string; alias?: string | null },
): string {
  let out = title;
  for (const t of [home, away]) {
    if (t?.name && t.alias && t.alias !== t.name) {
      out = out.split(t.name).join(t.alias);
    }
  }
  return out;
}

/**
 * Strip Polymarket's trailing season-record block ("Team Liquid (12-3)" →
 * "Team Liquid"). Polymarket bakes the record into the outcome label and
 * renders it on a separate line in their UI; we want the same.
 */
export function stripOutcomeRecord(label: string): string {
  if (!label) return label;
  return label.replace(/\s*\(\d+(?:-\d+){1,2}\)\s*$/, '').trim();
}

/**
 * Shorten outcome labels that come back as raw team names ("Club Atlético de
 * Madrid", "Arsenal FC") to the 3-letter ticker Polymarket uses on its sports
 * pages ("ATM", "ARS"). For non-team outcomes (Yes/No/Draw, totals, …) the
 * input is preserved. Used to keep the trade panel buttons compact instead of
 * letting full legal names blow out the row.
 */
export function abbreviateOutcomesForSports(
  outcomes: string[],
  home?: { name?: string; alias?: string | null; abbreviation?: string | null },
  away?: { name?: string; alias?: string | null; abbreviation?: string | null },
): string[] {
  if (!outcomes || outcomes.length === 0) return outcomes;
  const shortFor = (t?: { name?: string; alias?: string | null; abbreviation?: string | null }) => {
    if (!t?.name) return null;
    if (t.abbreviation) return String(t.abbreviation).toUpperCase();
    if (t.alias) return t.alias;
    return null;
  };
  return outcomes.map((rawLabel) => {
    const label = stripOutcomeRecord(rawLabel);
    const lower = label.toLowerCase();
    for (const t of [home, away]) {
      if (!t?.name) continue;
      const teamName = t.name.toLowerCase();
      if (lower === teamName || lower.includes(teamName)) {
        const short = shortFor(t);
        if (short) return short;
      }
    }
    return label;
  });
}

/**
 * Translate bookmaker shorthand into plain English. "O/U 1.5" → "Over 1.5 goals",
 * "Team (-1.5)" → "Team wins by 2+", "Team (+1.5)" → "Team loses by less than 2".
 * Mirrors the embed's `prettifyGameLineLabel` so both surfaces read alike.
 */
export function prettifyGameLineLabel(raw: string): string {
  const label = (raw || '').trim();
  if (!label) return '';
  const minus = label.match(/^(.+?)\s*\(\s*-\s*(\d+(?:\.\d+)?)\s*\)\s*$/);
  if (minus) return `${minus[1].trim()} wins by ${Math.max(1, Math.ceil(parseFloat(minus[2])))}+`;
  const plus = label.match(/^(.+?)\s*\(\s*\+\s*(\d+(?:\.\d+)?)\s*\)\s*$/);
  if (plus) return `${plus[1].trim()} loses by less than ${Math.max(1, Math.floor(parseFloat(plus[2]))) + 1}`;
  const ou = label.match(/^O\/U\s+(\d+(?:\.\d+)?)$/i);
  if (ou) return `Over ${ou[1]} goals`;
  return label;
}

/**
 * Esports leagues whose series unit Polymarket labels as "Maps"
 * (CS2/CSGO, Valorant). LoL/Dota2/etc. keep "Games". Used so
 * cleanMarketLabel reads "Map Handicap" / "Total Maps" on the right
 * pages and "Game Handicap" / "Total Games" on the others.
 */
export const MAPS_LEAGUES = new Set([
  'cs2', 'csgo', 'counter-strike',
  'val', 'valorant',
]);

export function isMapsLeague(league: string | undefined | null): boolean {
  return !!league && MAPS_LEAGUES.has(league.toLowerCase());
}

/** Clean market label like Polymarket: "Moneyline" not "Match Winner", "Map Handicap" not full title */
export function cleanMarketLabel(
  market: { sportsMarketType: string; groupItemTitle: string; question: string; negRisk?: boolean },
  siblingCount?: number,
  league?: string,
  t?: (key: string, vars?: Record<string, string | number>) => string,
): string {
  // Default `t` is identity-on-key when not supplied (allows the function
  // to be used outside React render — e.g. tests). Real callers pass the
  // translator from useT() so labels follow the active locale.
  const tr = t || ((k: string) => k);
  const type = market.sportsMarketType;
  const title = market.groupItemTitle || market.question;
  // seriesUnit feeds `marketLabel.totalPrefix` ("Total {what}"). Resolve
  // through i18n so ES gets "Total Mapas / Total Juegos" instead of the
  // hardcoded English nouns.
  const seriesUnit = isMapsLeague(league) ? tr("marketLabel.unit.mapsCap") : tr("marketLabel.unit.gamesCap");

  // Neg-risk moneyline (3-way): show team/outcome name, not generic "Moneyline"
  // Neg-risk 3-way moneyline (soccer Real/Draw/Barça): each row is its
  // own market with outcomes ["Yes","No"], so we surface the team name
  // from the title. For binary 2-way moneylines (tennis, esports — both
  // outcomes are team names) we fall through to the CLEAN_LABELS map
  // and render the row as "Moneyline" instead of duplicating "Match
  // Winner".
  const outcomesAreYesNo = Array.isArray((market as any).outcomes)
    && (market as any).outcomes.length === 2
    && (market as any).outcomes.every((o: string) => /^(yes|no)$/i.test(o));
  if (type === 'moneyline' && siblingCount && siblingCount > 1 && outcomesAreYesNo) {
    const cleaned = title.replace(/\s*\(.*\)\s*$/, '');
    return prettifyGameLineLabel(cleaned || title);
  }

  // For corners / totals, the whole tab is already called "Corners" /
  // "Match Total", so we only need the line — "Over or under 10.5 corners"
  // avoids bookmaker jargon (O/U) that casual users don't recognise.
  const cornerLine = market.sportsMarketType === 'total_corners' && typeof (market as any).line === 'number'
    ? `Over or under ${(market as any).line} corners`
    : null;

  // Per-game over/under kill markets read like "Total Kills Over/Under
  // 16.5 in Game 1?" — collapse to "Kill Totals {line}" since the tab
  // header already says which game.
  const killLineMatch = market.sportsMarketType === 'kill_over_under_game'
    ? title.match(/Over\/Under\s+(\d+(?:\.\d+)?)/i)
    : null;

  const totalsLabel = title.match(/Games/i)
    ? tr("marketLabel.totalPrefix", { what: seriesUnit })
    : title.replace(/^O\/U\s+\S+\s+/, `${tr("marketLabel.totalPrefix", { what: '' }).replace(/\s*\{what\}\s*/, '').trim() || 'Total'} `);
  const CLEAN_LABELS: Record<string, string> = {
    moneyline: tr("marketLabel.moneyline"),
    child_moneyline: title,
    // Series totals — gamma's title says "O/U 2.5 Games" generically,
    // but PM displays "Total Maps" on Valorant / CS2 and "Total Games"
    // on LoL / Dota2. seriesUnit is league-aware (see MAPS_LEAGUES).
    totals: totalsLabel,
    map_handicap: isMapsLeague(league) ? tr("marketLabel.mapHandicap") : tr("marketLabel.gameHandicap"),
    spreads: typeof (market as any).line === 'number'
      ? tr("marketLabel.spreadValue", { value: (market as any).line > 0 ? `+${(market as any).line}` : String((market as any).line) })
      : tr("marketLabel.spread"),
    total_corners: cornerLine || tr("marketLabel.totalCorners"),
    both_teams_score: tr("marketLabel.bothTeamsToScore"),
    // LoL / esports labels — strip Polymarket's verbose phrasing so the
    // row reads like the PM /event page does. The tab header already
    // tells the user which game we're in.
    first_blood_game: tr("marketLabel.firstBlood"),
    kill_over_under_game: killLineMatch ? tr("marketLabel.killTotalsValue", { value: killLineMatch[1] }) : tr("marketLabel.killTotalsLabel"),
    lol_both_teams_baron: tr("marketLabel.bothTeamsBaron"),
    lol_both_teams_dragon: tr("marketLabel.bothTeamsDragon"),
    lol_both_teams_inhibitors: tr("marketLabel.bothTeamsInhibitors"),
    lol_odd_even_total_kills: tr("marketLabel.oddEvenKills"),
    lol_penta_kill: tr("marketLabel.pentaKill"),
    lol_quadra_kill: tr("marketLabel.quadraKill"),
    // Tennis sub-event winners — strip the long "Tournament: P1 vs P2 Set N
    // Winner" wrapping so the row reads "Set 1 Winner" / "Set Winner" /
    // "Match Winner" instead. Tab + outcome buttons already carry the
    // tournament/players context. Polymarket prefixes these with `tennis_`
    // for ATP/WTA matches.
    first_set_winner: tr("marketLabel.firstSetWinner"),
    tennis_first_set_winner: tr("marketLabel.firstSetWinner"),
    second_set_winner: tr("marketLabel.secondSetWinner"),
    tennis_second_set_winner: tr("marketLabel.secondSetWinner"),
    third_set_winner: tr("marketLabel.thirdSetWinner"),
    tennis_third_set_winner: tr("marketLabel.thirdSetWinner"),
    set_winner: tr("marketLabel.setWinner"),
    tennis_set_winner: tr("marketLabel.setWinner"),
    match_winner: tr("marketLabel.matchWinner"),
    tennis_match_winner: tr("marketLabel.matchWinner"),
    game_winner: tr("marketLabel.gameWinner"),
    // Baseball jargon — Yes on NRFI = no run scored in 1st inning.
    // Flipping the label to "No run in 1st inning" keeps Yes/No intuitive.
    nrfi: tr("marketLabel.nrfi"),
    yrfi: tr("marketLabel.yrfi"),
    cs2_odd_even_total_rounds: tr("marketLabel.oddEvenRounds"),
    cs2_odd_even_total_kills: tr("marketLabel.oddEvenKills"),
  };

  // Question-text fallbacks for markets PM doesn't tag with a known
  // sportsMarketType (typical for related "more-markets" buckets where
  // type comes through as 'other'). Surface a translated label so ES
  // shows "Ambos equipos marcan" instead of the literal English title.
  const cleanTitle = (title || '').trim();
  if (!CLEAN_LABELS[type]) {
    if (/^both\s+teams\s+to\s+score/i.test(cleanTitle)) return tr("marketLabel.bothTeamsToScore");
  }

  return prettifyGameLineLabel(CLEAN_LABELS[type] || title);
}
